export class ChatRequestError extends Error {
  constructor(
    message: string,
    public readonly code = 'SUPPORT_CHAT_UNCERTAIN',
    public readonly status = 503,
    public readonly browserContext: string | null = null
  ) {
    super(message)
  }
}

export type ChatResponse<T> = { data: T; browserContext: string }

export async function chatRequest<T>(
  options: {
    method?: 'GET' | 'POST' | 'DELETE'
    query?: URLSearchParams
    browserContext?: string
    body?: unknown
    signal?: AbortSignal
  } = {}
): Promise<ChatResponse<T>> {
  const timeout = AbortSignal.timeout(45_000)
  const signal = options.signal
    ? AbortSignal.any([options.signal, timeout])
    : timeout
  let response: Response
  try {
    response = await fetch(
      `/api/support-chat${options.query ? `?${options.query}` : ''}`,
      {
        method: options.method ?? 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          'X-Support-Chat': '1',
          ...(options.browserContext
            ? { 'X-Support-Chat-Context': options.browserContext }
            : {}),
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        },
        ...(options.body ? { body: JSON.stringify(options.body) } : {}),
        signal,
      }
    )
  } catch {
    throw new ChatRequestError(
      'We could not confirm this request. Your saved message can be retried safely.'
    )
  }
  const browserContext = response.headers.get('X-Support-Chat-Context')
  const payload: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const error = payload as { error?: string; code?: string } | null
    throw new ChatRequestError(
      typeof error?.error === 'string'
        ? error.error
        : 'Chat is temporarily unavailable. Please try again.',
      typeof error?.code === 'string' ? error.code : 'SUPPORT_CHAT_UNCERTAIN',
      response.status,
      browserContext
    )
  }
  if (
    !payload ||
    typeof payload !== 'object' ||
    !browserContext ||
    !/^[a-f0-9]{32}$/.test(browserContext)
  ) {
    throw new ChatRequestError(
      'We could not confirm the response. Retry the same request.'
    )
  }
  return { data: payload as T, browserContext }
}

export async function withChatLock<T>(action: () => Promise<T>): Promise<T> {
  if (typeof navigator === 'undefined' || !navigator.locks) {
    return Promise.reject(
      new Error(
        'This browser cannot safely recover messages. Please use a current browser or contact us by email.'
      )
    )
  }
  // Serializes bootstrap, sends and ending access across same-origin browser tabs.
  return navigator.locks.request('vi-support-messenger', action)
}

export function chatError(error: unknown) {
  return error instanceof Error
    ? error.message
    : 'Something went wrong. Please try again.'
}

export function chatAccessLost(error: unknown) {
  return error instanceof ChatRequestError && [401, 403].includes(error.status)
}
